import React, { useEffect, memo, useState } from "react";
import styled from "styled-components";
import { Divider } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import Box from "../Box";
import TabsBar from "../TabsBar";
import StoreName from "../StoreName";
import StoreStatusBadge from "../StoreStatusBadge";
import SideBar from "../SideBar";
import RangesSelector from "../RangesSelector/RangesSelector";

import useScrollDirection from "../../utils/useScrollDirection";
import useBreakPoint from "../../utils/useBreakPoint";

import { RootState } from "../../store";
import { handleModeSlice } from "../../slices/modeSlice";
import {
  getModeFromPath,
  isRegionMode,
  isStoreMode,
} from "../../utils/modeUtils";

import { ReactComponent as LocationIcon } from "../../media/location.svg";
import { isEmailInWhitelist } from "../../utils/whiteListUtils";
import { useAuth } from "../../Auth/AuthProvider";
import useLandscape from "../../utils/useLandscape";
import ConnectedStoresCard from "../ConnectedStoresCard/ConnectedStoresCard";

/* ---------------- STYLES ---------------- */

const HeaderWrapper = styled.header`
  position: sticky;
  top: 0;
  z-index: 2;
  width: 100%;
  background: transparent;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  width: 100%;
  background: white;
  box-sizing: border-box;
`;

const Inner = styled.div`
  max-width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
`;

const Separator = styled.div`
  height: 1px;
  background: #e5e7eb;
`;

const MobileShadowBox = styled(Box)<{ $isScrolled: boolean }>`
  background: #fff;
  box-shadow: 0px 4px 4px rgba(0,0,0,0.25);
  height: ${({ $isScrolled }) => ($isScrolled ? "4.8rem" : "7rem")};
  transition: height 500ms;
`;

/* ---------------- MOBILE VIEW ---------------- */

const MobileHeaderContent = memo(
  ({
    scrollDirection,
    mode,
    isCaLiveRoute,
  }: {
    scrollDirection: boolean;
    mode: "store" | "region" | "alert";
    isCaLiveRoute: boolean;
  }) => {
    return (
      <MobileShadowBox
        display="flex"
        flexDirection="column"
        width="100%"
        data-testid="header"
        $isScrolled={scrollDirection}
      >
        <Box
          p={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <StoreName $isScrolled={scrollDirection} />
          {!isCaLiveRoute && <StoreStatusBadge />}
        </Box>

        {!scrollDirection && (
          <Divider style={{ margin: 0 }} />
        )}

        {(isStoreMode(mode) || isRegionMode(mode)) && !isCaLiveRoute && (
          <TabsBar />
        )}
      </MobileShadowBox>
    );
  }
);

MobileHeaderContent.displayName = "MobileHeaderContent";

/* ---------------- DESKTOP VIEW ---------------- */

const DesktopHeaderContent = memo(
  ({
    mode,
    isCaLiveRoute,
    isMobileLandscape,
    totalStores,
    connectedStores,
  }: {
    mode: "store" | "region" | "alert";
    isCaLiveRoute: boolean;
    isMobileLandscape?: boolean;
    totalStores?: number;
    connectedStores?: number;
  }) => {
    return (
      <>
      <Content>
        <Inner>
          <Box
            p={1}
            height="56px"
            display="flex"
            alignItems="center"
            justifyContent={isMobileLandscape ? "space-between" : "flex-start"}
          >
            <StoreName $isScrolled={false} />
            {!isCaLiveRoute && (isMobileLandscape ? (
              <Box display="flex" alignItems="flex-start">
                <Box pl={2} pr={1} pt="2px" display="flex">
                  <LocationIcon
                    height={16}
                    width={16}
                  />
                </Box>
                <StoreStatusBadge />
              </Box>
            ) : (
              <>
                <Box pl={2} pr={1} display="flex">
                  <LocationIcon
                    height={16}
                    width={16}
                  />
                </Box>
                <StoreStatusBadge />
              </>
            ))}
            {isCaLiveRoute && (
              <Box ml="auto" display="flex" alignItems="center">
                <ConnectedStoresCard
                  totalStores={totalStores}
                  connectedStores={connectedStores}
                />
              </Box>
            )}
          </Box>
        </Inner>

        {!isCaLiveRoute && (
          <>
            <Separator />

            <Inner>
              <Box
                p={1}
                height="56px"
                display="flex"
                alignItems="center"
              >
                <TabsBar />
              </Box>
            </Inner>
          </>
        )}

        <Separator />
      </Content>
      </>
    );
  }
);

DesktopHeaderContent.displayName = "DesktopHeaderContent";

/* ---------------- Main HEADER ---------------- */

const useIsMobileLandscape = () => {
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia("(max-height: 560px) and (orientation: landscape)");
    setIsMobileLandscape(mql.matches);
    const listener = (e: MediaQueryListEvent) => setIsMobileLandscape(e.matches);
    mql.addEventListener("change", listener);
    return () => mql.removeEventListener("change", listener);
  }, []);

  return isMobileLandscape;
};

const Header = () => {
  const { user } = useAuth();
  const currentView = useBreakPoint();
  const isLandscape = useLandscape();
  const isMobileLandscape = useIsMobileLandscape();
  const isDesktop =
    currentView === "desktop" || currentView === "tablet" || (currentView === "phone" && isLandscape) || isMobileLandscape;
  const scrollDirection = useScrollDirection();
  const [storesCounters, setStoresCounters] = useState<{
    totalStores?: number;
    connectedStores?: number;
  }>();

  const isVisible = useSelector(
    (state: RootState) => state.sideBar.value
  );

  const dispatch = useDispatch();
  const location = useLocation();

  const pathname = location?.pathname ?? "";
  const isCaLiveRoute = pathname === "/ca-live";

  const mode = getModeFromPath(
    location?.pathname ?? ""
  );

  const showDesktopRangesSelector =
    isDesktop &&
    isEmailInWhitelist("ECOMMERCE_NEW_IHM_WHITE_LIST", user) &&
    isStoreMode(mode) &&
    pathname.includes("/commercial");

  useEffect(() => {
    dispatch(handleModeSlice(mode));
  }, [dispatch, mode]);

  useEffect(() => {
    const handleStoresCounters = (event: Event) => {
      const customEvent = event as CustomEvent<{
        totalStores?: number;
        connectedStores?: number;
      }>;
      if (customEvent.detail) {
        setStoresCounters(customEvent.detail);
      }
    };

    window.addEventListener("caLiveStoresCounters", handleStoresCounters);
    return () => {
      window.removeEventListener("caLiveStoresCounters", handleStoresCounters);
    };
  }, []);

  const shouldRenderDesktopHeader = isMobileLandscape || (isEmailInWhitelist("ECOMMERCE_NEW_IHM_WHITE_LIST", user) && isDesktop);

  return (
    <>
      <HeaderWrapper>
        <SideBar isVisible={isVisible} />

        {isVisible && (!isDesktop || isMobileLandscape) && (
          <Box className="blur" />
        )}

        {!shouldRenderDesktopHeader ? (
          <MobileHeaderContent
            scrollDirection={scrollDirection}
            mode={mode}
            isCaLiveRoute={isCaLiveRoute}
          />
        ) : (
          <DesktopHeaderContent
            mode={mode}
            isCaLiveRoute={isCaLiveRoute}
            isMobileLandscape={isMobileLandscape}
            totalStores={storesCounters?.totalStores}
            connectedStores={storesCounters?.connectedStores}
          />
        )}
      </HeaderWrapper>

      {showDesktopRangesSelector && (
        <RangesSelector />
      )}
    </>
  );
};

export default Header;